// Site-wide content for HAS Universal Management Consultancy LLC
import {
  Briefcase, BarChart3, Megaphone, Newspaper, Building2,
  Plane, Truck, Compass, ShieldCheck, LineChart, Globe2,
  Handshake, Target, Search, Sparkles, ArrowUpRight, Rocket,
} from "lucide-react";

export const COMPANY = {
  name: "HAS Universal Management Consultancy LLC",
  short: "HAS UMC",
  tagline: "Business Growth | Market Intelligence | Strategic Advisory",
  trust: "Based in Dubai | Serving UAE, India & International Markets",
  phone: "+971 56 213 5509",
  email: "info@hasumc.com",
  website: "www.hasumc.com",
  location: "Deira, Dubai, United Arab Emirates",
  logo: "https://customer-assets.emergentagent.com/job_strategic-growth-hub-4/artifacts/egexfvbw_A0994AC6-6E3A-460C-ABBA-A50C4D00E244.PNG",
};

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/approach", label: "Approach" },
  { to: "/contact", label: "Contact" },
];

export const HERO_HIGHLIGHTS = [
  "Management Consultancy",
  "Marketing Research & Consultancy",
  "Advertising Research & Consultancy",
  "Media Studies & Consultancy",
  "Real Estate Consultancy",
  "Tourism & Recreation Consultancy",
  "Logistics Consultancy",
];

export const SERVICES = [
  {
    id: "management",
    title: "Management Consultancy",
    icon: Briefcase,
    summary:
      "Strategy, advisory and growth planning that strengthens performance and unlocks new markets.",
    points: [
      "Business Strategy Development",
      "Corporate Advisory",
      "Business Growth Planning",
      "Market Entry Strategy",
      "Organizational Development",
      "Business Expansion Consulting",
    ],
  },
  {
    id: "marketing-research",
    title: "Marketing Research & Consultancy",
    icon: BarChart3,
    summary:
      "Data-driven research that converts insight into market share and clear go-to-market direction.",
    points: [
      "Market Research",
      "Consumer Insights",
      "Competitor Analysis",
      "Feasibility Studies",
      "Brand Positioning",
      "Go-To-Market Strategies",
    ],
  },
  {
    id: "advertising-research",
    title: "Advertising Research & Consultancy",
    icon: Megaphone,
    summary:
      "Evidence-led campaign intelligence that maximises media spend and brand recall.",
    points: [
      "Campaign Effectiveness Studies",
      "Media Planning Advisory",
      "Advertising Strategy",
      "Consumer Response Analysis",
      "Brand Awareness Research",
    ],
  },
  {
    id: "media-studies",
    title: "Media Studies & Consultancy",
    icon: Newspaper,
    summary:
      "Executive positioning, reputation and thought leadership for leaders and corporate brands.",
    points: [
      "Executive Positioning",
      "Corporate Visibility Strategy",
      "Reputation Management",
      "Thought Leadership Development",
      "Media Strategy Consulting",
    ],
  },
  {
    id: "real-estate",
    title: "Real Estate Consultancy",
    icon: Building2,
    summary:
      "From project positioning to investor outreach — strategic clarity for developers and asset owners.",
    points: [
      "Project Positioning",
      "Market Research",
      "Project Marketing Strategy",
      "Investor Outreach Strategy",
      "International Expansion Advisory",
    ],
  },
  {
    id: "tourism",
    title: "Tourism & Recreation Consultancy",
    icon: Plane,
    summary:
      "Destination, hospitality and tourism advisory that turns experiences into sustainable revenue.",
    points: [
      "Tourism Development Strategy",
      "Destination Branding",
      "Hospitality Growth Consulting",
      "Tourism Marketing Solutions",
    ],
  },
  {
    id: "logistics",
    title: "Logistics Consultancy",
    icon: Truck,
    summary:
      "Supply chain, fleet and operations optimisation engineered around cost, speed and resilience.",
    points: [
      "Supply Chain Optimization",
      "Logistics Strategy",
      "Fleet Management Advisory",
      "Process Improvement",
      "Cost Optimization",
    ],
  },
];

export const WHY_CHOOSE = [
  {
    title: "Industry Expertise",
    desc: "Knowledge across multiple industries and sectors.",
    icon: ShieldCheck,
  },
  {
    title: "Research-Driven Approach",
    desc: "Every recommendation backed by data and market intelligence.",
    icon: LineChart,
  },
  {
    title: "Business Growth Focus",
    desc: "Solutions designed to create measurable business outcomes.",
    icon: Target,
  },
  {
    title: "Regional & Global Perspective",
    desc: "Strong understanding of UAE, India, and international markets.",
    icon: Globe2,
  },
  {
    title: "Long-Term Partnerships",
    desc: "Building relationships that create sustainable success.",
    icon: Handshake,
  },
];

export const INDUSTRIES = [
  "Real Estate",
  "Hospitality & Tourism",
  "Logistics & Transportation",
  "Retail & E-Commerce",
  "Professional Services",
  "Manufacturing",
  "Startups & SMEs",
  "Family Businesses",
  "Media & Entertainment",
];

export const APPROACH_STEPS = [
  { num: "01", title: "Discover", desc: "Understanding business challenges and opportunities.", icon: Search },
  { num: "02", title: "Analyze",  desc: "Research, assessment, and intelligence gathering.", icon: Compass },
  { num: "03", title: "Strategize", desc: "Developing practical growth solutions.", icon: Sparkles },
  { num: "04", title: "Execute", desc: "Supporting implementation and business development.", icon: ArrowUpRight },
  { num: "05", title: "Grow", desc: "Achieving sustainable business success.", icon: Rocket },
];

export const STATS = [
  { value: 7,   suffix: "+", label: "Consulting Practices" },
  { value: 9,   suffix: "+", label: "Industries Served" },
  { value: 3,   suffix: "",  label: "Markets — UAE · India · Global" },
  { value: 100, suffix: "%", label: "Research-Driven Recommendations" },
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1651467606797-e1c660cf3fda?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxkdWJhaSUyMHNreWxpbmUlMjBkdXNrfGVufDB8fHx8MTc4MjIwOTgzMnww&ixlib=rb-4.1.0&q=85",
  boardroom: "https://images.unsplash.com/photo-1779700210487-a01758a3c55a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxleGVjdXRpdmUlMjBib2FyZHJvb20lMjBuaWdodHxlbnwwfHx8fDE3ODIyMDk4MzJ8MA&ixlib=rb-4.1.0&q=85",
  meeting: "https://images.pexels.com/photos/7433853/pexels-photo-7433853.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  goldAbstract: "https://images.pexels.com/photos/31622917/pexels-photo-31622917.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};
